/**
 * Iter 67u — Vendor Stripe auto-pay card.
 *
 * Enrolls the vendor's monthly-per-car subscription into Stripe auto-renew:
 *  • Enable auto-pay → POST /vendor/billing/subscribe/stripe → redirect to
 *    Stripe Checkout in `subscription` mode. On return we confirm the saved
 *    subscription via /vendor/billing/subscribe/stripe/confirm/{session_id}.
 *  • Manage saved card → POST /vendor/billing/portal → Stripe Customer Portal.
 *  • Cancel auto-pay → POST /vendor/billing/unsubscribe/stripe.
 *
 * Only renders for vendors on the `monthly_per_car` plan. Falls back to a soft
 * "Stripe not configured" notice in sandbox mode.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Repeat, ShieldCheck, ExternalLink, Loader2, CheckCircle2, AlertCircle, X,
  Wallet,
} from "lucide-react";

export default function VendorStripeAutoPayCard() {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendor/billing");
      setBilling(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // After Stripe Checkout returns with ?stripe_sub=success&session_id=cs_xxx
  // we confirm the saved subscription so the local state flips to "active"
  // immediately (instead of waiting for the webhook).
  useEffect(() => {
    const status = searchParams.get("stripe_sub");
    const sessionId = searchParams.get("session_id");
    if (status === "success" && sessionId) {
      (async () => {
        try {
          await api.post(`/vendor/billing/subscribe/stripe/confirm/${sessionId}`);
          toast.success("Auto-pay enabled — your card will be charged monthly.");
        } catch (e) {
          // Non-fatal: webhook will eventually sync.
        } finally {
          searchParams.delete("stripe_sub");
          searchParams.delete("session_id");
          setSearchParams(searchParams, { replace: true });
          load();
        }
      })();
    } else if (status === "cancel") {
      toast.info("Auto-pay enrollment cancelled.");
      searchParams.delete("stripe_sub");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, []);

  const enable = async () => {
    setWorking(true);
    try {
      const { data } = await api.post("/vendor/billing/subscribe/stripe", {});
      if (data?.checkout_url) {
        window.location.assign(data.checkout_url);
      } else {
        toast.error("Stripe didn't return a checkout URL.");
        setWorking(false);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setWorking(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel auto-pay? Your card will not be charged at the next billing cycle. You can re-enable anytime.")) return;
    setWorking(true);
    try {
      await api.post("/vendor/billing/unsubscribe/stripe", {});
      toast.success("Auto-pay cancelled.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(false);
    }
  };

  const openPortal = async () => {
    setWorking(true);
    try {
      const { data } = await api.post("/vendor/billing/portal", {});
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWorking(false);
    }
  };

  if (loading || !billing) return null;
  if (billing.mode !== "monthly_per_car") return null;

  const enrolled = !!billing.stripe_subscription_id && billing.subscription_status === "active";
  const monthly = (billing.monthly_fee_estimate ?? 0).toFixed(2);

  return (
    <div className="rounded-3xl border border-violet-100 bg-gradient-to-br from-violet-50/40 via-white to-indigo-50/40 p-6" data-testid="vendor-stripe-autopay-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-11 w-11 rounded-2xl bg-violet-100 ring-1 ring-violet-200 flex items-center justify-center shrink-0">
          <Repeat className="h-5 w-5 text-violet-700" />
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-semibold leading-tight">Stripe auto-pay</h3>
          <p className="text-sm text-neutral-600 mt-0.5">
            Save a card with Stripe and your monthly subscription is paid automatically on the renewal date. PCI handled by Stripe — your card details never touch RentalCar.al.
          </p>
        </div>
        {enrolled ? (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700" data-testid="autopay-status-active">
            <CheckCircle2 className="h-3 w-3" /> Active
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-neutral-100 text-neutral-600" data-testid="autopay-status-off">
            <AlertCircle className="h-3 w-3" /> Not enrolled
          </span>
        )}
      </div>

      <div className="rounded-2xl border border-violet-100 bg-white/70 p-4 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700">Fee per car</div>
          <div className="font-semibold mt-0.5">€{(billing.value ?? 0).toFixed(2)}/mo</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700">Active cars</div>
          <div className="font-semibold mt-0.5">{billing.active_car_count ?? 0}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700">Monthly total</div>
          <div className="font-semibold mt-0.5">€{monthly}/mo</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {!enrolled ? (
          <Button
            onClick={enable}
            disabled={working}
            className="rounded-full bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 hover:from-indigo-700 hover:via-violet-700 hover:to-purple-700 text-white"
            data-testid="autopay-enable-btn"
          >
            {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
            Enable auto-pay
          </Button>
        ) : (
          <>
            <Button
              onClick={openPortal}
              disabled={working}
              variant="outline"
              className="rounded-full border-violet-200 text-violet-700 hover:bg-violet-50"
              data-testid="autopay-manage-card-btn"
            >
              {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-2" />}
              Manage saved card
            </Button>
            <Button
              onClick={cancel}
              disabled={working}
              variant="ghost"
              className="rounded-full text-rose-600 hover:bg-rose-50"
              data-testid="autopay-cancel-btn"
            >
              <X className="h-4 w-4 mr-1.5" /> Cancel auto-pay
            </Button>
          </>
        )}
        <div className="text-[11px] text-neutral-500 flex items-center gap-1 ml-auto">
          <Wallet className="h-3 w-3 text-emerald-600" />
          Charged each month · invoices marked Paid automatically · failed-payment email alert
        </div>
      </div>
    </div>
  );
}
